// Admin · Channel Connections
// Manages the provider-neutral channel_connection abstraction. Lets an
// owner/admin add a DEV_MANUAL connection (Gate 5.1 opener path) for
// Facebook and other publish channels, rotate the stored secret, or
// revoke a connection. Secrets are POSTed once and never surfaced back
// (envelopes only). Health states drive scheduler pause behavior.
import React, { useCallback, useEffect, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const HEALTH_TONE = {
  HEALTHY: "success",
  UNKNOWN: "muted",
  EXPIRING_SOON: "warn",
  REAUTH_REQUIRED: "danger",
  ERROR: "danger",
};

// Human-readable explanation of each health state. Rendered as a
// tooltip on the health pill AND (in expanded form) inline for any
// non-HEALTHY state so the operator never has to guess what a code means.
const HEALTH_EXPLAINER = {
  HEALTHY: "This connection was validated against the provider recently and is publish-ready.",
  UNKNOWN: "This connection has not been validated against the provider yet. It becomes HEALTHY the first time Preflight step 6 (channel_validation) or an actual publish succeeds.",
  EXPIRING_SOON: "The provider token will expire soon. Use Reconnect to refresh it before the next scheduled publish.",
  REAUTH_REQUIRED: "The provider rejected the token on the last call. Use Reconnect (OAuth) or Rotate secret to attach a fresh token — the scheduler has paused any publishes bound to this connection.",
  ERROR: "The last call to the provider failed for a non-auth reason (network, rate limit, or provider outage). See the reason below and retry.",
};

const STATUS_TONE = {
  ACTIVE: "success",
  REVOKED: "muted",
  EXPIRED: "warn",
};

const CHANNEL_LABEL = {
  facebook: "Facebook Page",
  instagram: "Instagram (via Facebook OAuth)",
  youtube: "YouTube Channel",
  tiktok: "TikTok Business Account",
  pinterest: "Pinterest Business",
  email: "Email (Resend)",
  manual_export: "Manual export",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch (e) {
    return String(iso);
  }
}

function formatRelative(iso) {
  if (!iso) return "never";
  const now = Date.now();
  const then = new Date(iso).getTime();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/* -------------------------------------------------------------------------- */
/*  Create connection modal                                                   */
/* -------------------------------------------------------------------------- */


// P5.4 (2026-11-03) · Item 6 — visible live-mode toggle per connection.
// Flips the per-connection `live_mode_enabled` flag. Both this AND
// the env secret PUBLISH_LIVE_MODE_AUTHORIZED must be on for an
// adapter to attempt a real post; otherwise the adapter transparently
// falls back to a test-mode receipt tagged with
// `test_mode_fallback_reason`.
// Round 14-B · OAuth scopes as short chips. Google scopes are URLs
// ("https://www.googleapis.com/auth/youtube.upload"); strip the prefix for
// the label and keep the full value in the tooltip. Display-only.
const GOOGLE_SCOPE_PREFIX = "https://www.googleapis.com/auth/";
export function shortScope(scope) {
  const s = String(scope || "");
  return s.startsWith(GOOGLE_SCOPE_PREFIX) ? s.slice(GOOGLE_SCOPE_PREFIX.length) : s;
}
export function ScopeChips({ scopes, testId, emptyLabel = "\u2014" }) {
  const list = Array.isArray(scopes) ? scopes.filter(Boolean) : [];
  if (!list.length) return <div data-testid={testId}>{emptyLabel}</div>;
  return (
    <div className="cc-scope-chips" data-testid={testId}>
      {list.map((sc) => (
        <span
          key={sc}
          className="cc-scope-chip"
          title={sc}
          aria-label={sc}
          data-testid={`${testId}-chip`}
        >
          {shortScope(sc)}
        </span>
      ))}
    </div>
  );
}


function LiveModeToggle({ connection, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const enabled = !!connection.live_mode_enabled;
  async function toggle() {
    if (busy) return;
    const next = !enabled;
    const confirmText = next
      ? `Enable LIVE MODE for ${connection.display_name}? Real posts can go out from this connection once the deployment-wide PUBLISH_LIVE_MODE_AUTHORIZED env flag is also on.`
      : `Disable live mode for ${connection.display_name}? Any publish-now with test_mode=false will fall back to a test-mode receipt.`;
    if (!window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await api.put(
        `/publish/channel-connections/${connection.id}/live-mode`,
        { live_mode_enabled: next },
      );
      if (onChanged) await onChanged();
    } catch (e) {
      alert(
        e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e.message,
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      className={`bo-btn ${enabled ? "bo-btn-primary" : "bo-btn-ghost"}`}
      data-testid={`connection-live-mode-${connection.id}`}
      onClick={toggle}
      disabled={busy}
      title={
        enabled
          ? "Live mode is ON for this connection"
          : "Live mode is OFF — all posts fall back to test-mode receipts"
      }
      style={{ padding: "2px 10px", fontSize: 11, lineHeight: 1.4 }}
    >
      {busy ? "…" : enabled ? "Live mode: ON" : "Live mode: OFF"}
    </button>
  );
}

// P5.5 (2026-11-04) — Pinterest-only · set the default board id used
// by the publisher (and acceptance dry-run) when the channel_config
// has no explicit board_id. The provider_ref.board_id picked at OAuth
// time is auto-promoted to default_board_id by the round-2 migration
// so most connections already show a value here.
function PinterestDefaultBoardChip({ connection, onChanged }) {
  const [busy, setBusy] = React.useState(false);
  const [editing, setEditing] = React.useState(false);
  const [value, setValue] = React.useState(
    connection.default_board_id || "",
  );
  if ((connection.channel || "").toLowerCase() !== "pinterest") return null;
  async function save() {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      alert("default_board_id cannot be blank. Paste a Pinterest board id.");
      return;
    }
    setBusy(true);
    try {
      await api.put(
        `/publish/channel-connections/${connection.id}/default-board`,
        { default_board_id: trimmed },
      );
      setEditing(false);
      if (onChanged) await onChanged();
    } catch (e) {
      alert(
        e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e.message,
      );
    } finally {
      setBusy(false);
    }
  }
  if (editing) {
    return (
      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
        <input
          className="bo-input"
          data-testid={`connection-default-board-input-${connection.id}`}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="board_id (e.g. 1234567890123)"
          style={{ padding: "2px 6px", fontSize: 11, width: 180 }}
        />
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          data-testid={`connection-default-board-save-${connection.id}`}
          onClick={save}
          disabled={busy}
          style={{ padding: "2px 10px", fontSize: 11 }}
        >
          {busy ? "…" : "Save"}
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          data-testid={`connection-default-board-cancel-${connection.id}`}
          onClick={() => { setEditing(false); setValue(connection.default_board_id || ""); }}
          disabled={busy}
          style={{ padding: "2px 10px", fontSize: 11 }}
        >
          Cancel
        </button>
      </span>
    );
  }
  return (
    <button
      type="button"
      className={`bo-btn ${connection.default_board_id ? "bo-btn-ghost" : "bo-btn-warn"}`}
      data-testid={`connection-default-board-${connection.id}`}
      onClick={() => setEditing(true)}
      title={
        connection.default_board_id
          ? `Default board: ${connection.default_board_id}`
          : "No default board set — dry-run and publisher will fail with board_id_required"
      }
      style={{ padding: "2px 10px", fontSize: 11, lineHeight: 1.4 }}
    >
      {connection.default_board_id
        ? `Board: ${String(connection.default_board_id).slice(-6)}`
        : "Set default board"}
    </button>
  );
}

function CreateConnectionForm({ onCancel, onCreated }) {
  const [channel, setChannel] = useState("facebook");
  const [displayName, setDisplayName] = useState("");
  const [pageId, setPageId] = useState("");
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = {
        channel,
        connection_type: "DEV_MANUAL",
        display_name: displayName.trim(),
        is_default: isDefault,
        provider_ref: {},
        secret_plaintext: pageAccessToken.trim(),
      };
      if (channel === "facebook") {
        body.provider_ref.page_id = pageId.trim();
      }
      const r = await api.post("/publish/channel-connections", body);
      onCreated(r.data);
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="create-connection-form"
      style={{ display: "grid", gap: 14 }}
    >
      <ErrorBanner error={error} testId="create-connection-error" />

      <div className="bo-field">
        <label className="bo-label">Channel</label>
        <select
          className="bo-input"
          data-testid="create-connection-channel"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
        >
          <option value="facebook">Facebook (Meta Pages)</option>
          <option value="email">Email (Resend)</option>
        </select>
      </div>

      <div className="bo-field">
        <label className="bo-label">Display name</label>
        <input
          className="bo-input"
          data-testid="create-connection-display-name"
          placeholder="e.g. Precipice Health Page"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
        <div className="bo-hint">
          Shown in the connections list; safe to reuse when rotating.
        </div>
      </div>

      {channel === "facebook" && (
        <div className="bo-field">
          <label className="bo-label">Facebook Page ID</label>
          <input
            className="bo-input"
            data-testid="create-connection-page-id"
            placeholder="e.g. 100081234567890"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            required
          />
          <div className="bo-hint">
            Find in Meta Business Suite → Settings → Page Info → Page ID.
          </div>
        </div>
      )}

      <div className="bo-field">
        <label className="bo-label">
          {channel === "facebook" ? "Page Access Token" : "API Key"}
        </label>
        <div style={{ position: "relative" }}>
          <input
            className="bo-input"
            data-testid="create-connection-secret"
            type={showToken ? "text" : "password"}
            placeholder="Paste the long-lived token from Meta Graph API Explorer"
            value={pageAccessToken}
            onChange={(e) => setPageAccessToken(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
            style={{ paddingRight: 76 }}
          />
          <button
            type="button"
            className="bo-btn bo-btn-ghost"
            data-testid="toggle-token-visibility"
            onClick={() => setShowToken((v) => !v)}
            style={{
              position: "absolute", right: 6, top: 6, height: 30,
              padding: "0 10px", fontSize: 12,
            }}
          >
            {showToken ? "Hide" : "Show"}
          </button>
        </div>
        <div className="bo-hint">
          Encrypted at rest with Fernet. This value is never returned by any GET
          endpoint and is never written to logs, audit rows, or Publish Receipts.
          You'll only see a short non-reversible fingerprint after saving.
        </div>
      </div>

      <label
        className="bo-check"
        data-testid="create-connection-default-toggle"
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
      >
        <input
          type="checkbox"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
        <span>Use as the default connection for this channel</span>
      </label>

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          data-testid="create-connection-cancel"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bo-btn bo-btn-primary"
          data-testid="create-connection-submit"
          disabled={busy || !displayName.trim() || !pageAccessToken.trim()}
        >
          {busy ? "Saving…" : "Save connection"}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Rotate connection modal                                                   */
/* -------------------------------------------------------------------------- */

function RotateSecretForm({ connection, onCancel, onRotated }) {
  const [newSecret, setNewSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post(
        `/publish/channel-connections/${connection.id}/rotate`,
        { new_secret_plaintext: newSecret.trim() },
      );
      onRotated(r.data);
    } catch (err) {
      setError(formatApiError(err));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} data-testid="rotate-secret-form" style={{ display: "grid", gap: 14 }}>
      <ErrorBanner error={error} testId="rotate-secret-error" />
      <div className="bo-hint" data-testid="rotate-secret-context">
        Rotating <strong>{connection.display_name}</strong>. The old secret will
        be replaced immediately; any publish attempts already in flight complete
        with the old token, and everything queued after this point uses the new one.
      </div>
      <div className="bo-field">
        <label className="bo-label">New secret</label>
        <input
          className="bo-input"
          data-testid="rotate-secret-input"
          type="password"
          placeholder="Paste the new token"
          value={newSecret}
          onChange={(e) => setNewSecret(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          required
        />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button type="button" className="bo-btn bo-btn-ghost" data-testid="rotate-secret-cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="bo-btn bo-btn-primary" data-testid="rotate-secret-submit" disabled={busy || !newSecret.trim()}>
          {busy ? "Rotating…" : "Rotate secret"}
        </button>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main page                                                                 */
/* -------------------------------------------------------------------------- */

export default function ChannelConnectionsAdmin() {
  const [connections, setConnections] = useState([]);
  const [fleet, setFleet] = useState(null); // Phase-0 Task 3: banner + per-row expiry
  const [approvals, setApprovals] = useState([]); // P1: platform approval per channel
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [state, setState] = useState({ loading: true, error: null });
  const [modal, setModal] = useState(null); // { kind: "create" | "rotate", connection? }
  const [flash, setFlash] = useState(null);
  // publish-87 round-11 · item D — preview sandbox credential profile.
  const [ttProfile, setTtProfile] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/publish/tiktok/credential-profile")
      .then((r) => { if (alive) setTtProfile(r.data); })
      .catch(() => { if (alive) setTtProfile(null); });
    return () => { alive = false; };
  }, []);
  // Per-provider busy state. A shared `oauthBusy` previously caused
  // every provider's "Connect" button to spin as soon as any of them
  // was clicked. Keyed by channel: "facebook" | "youtube" | "tiktok" |
  // "pinterest" | "reconnect:<id>".
  const [oauthBusyBy, setOauthBusyBy] = useState({});
  const isBusy = (k) => !!oauthBusyBy[k];
  const setBusy = (k, v) =>
    setOauthBusyBy((prev) => {
      const next = { ...prev };
      if (v) next[k] = true;
      else delete next[k];
      return next;
    });

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const q = new URLSearchParams();
      if (includeRevoked) q.set("include_revoked", "true");
      const [listResp, fleetResp, approvalsResp] = await Promise.all([
        api.get(`/publish/channel-connections?${q.toString()}`),
        api.get("/channel-connections/fleet-status").catch(() => ({ data: null })),
        api.get("/platform-approvals").catch(() => ({ data: { approvals: [] } })),
      ]);
      setConnections(listResp.data.connections || []);
      setFleet(fleetResp.data || null);
      setApprovals(approvalsResp.data.approvals || []);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, [includeRevoked]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Success/error flashes bounced back from the OAuth callback.
    const p = new URLSearchParams(window.location.search);
    const oauthResult = p.get("oauth_result");
    const oauthError = p.get("oauth_error");
    if (oauthResult) {
      setFlash(decodeURIComponent(oauthResult));
    } else if (oauthError) {
      setState((s) => ({ ...s, error: decodeURIComponent(oauthError) }));
    }
    if (oauthResult || oauthError) {
      // Clean the URL so refreshes don't re-flash.
      const u = new URL(window.location.href);
      u.searchParams.delete("oauth_result");
      u.searchParams.delete("oauth_error");
      window.history.replaceState({}, "", u.toString());
    }
  }, []);

  async function handleStartOAuth() {
    setBusy("facebook", true);
    setState((s) => ({ ...s, error: null }));
    try {
      const r = await api.get("/publish/oauth/facebook/start?intent=create");
      const { authorize_url } = r.data;
      window.location.assign(authorize_url);
    } catch (e) {
      setBusy("facebook", false);
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleStartYouTubeOAuth() {
    setBusy("youtube", true);
    setState((s) => ({ ...s, error: null }));
    try {
      const r = await api.get("/publish/oauth/youtube/start?intent=create");
      const { authorize_url } = r.data;
      window.location.assign(authorize_url);
    } catch (e) {
      setBusy("youtube", false);
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleStartTikTokOAuth() {
    setBusy("tiktok", true);
    setState((s) => ({ ...s, error: null }));
    try {
      const r = await api.get("/publish/oauth/tiktok/start?intent=create");
      const { authorize_url } = r.data;
      window.location.assign(authorize_url);
    } catch (e) {
      setBusy("tiktok", false);
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleStartPinterestOAuth() {
    setBusy("pinterest", true);
    setState((s) => ({ ...s, error: null }));
    try {
      const r = await api.get("/publish/oauth/pinterest/start?intent=create");
      const { authorize_url } = r.data;
      window.location.assign(authorize_url);
    } catch (e) {
      setBusy("pinterest", false);
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleReconnect(conn) {
    const key = `reconnect:${conn.id}`;
    setBusy(key, true);
    setState((s) => ({ ...s, error: null }));
    try {
      const r = await api.post(
        `/publish/channel-connections/${conn.id}/reconnect`,
      );
      const { authorize_url } = r.data;
      window.location.assign(authorize_url);
    } catch (e) {
      setBusy(key, false);
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  async function handleRevoke(conn) {
    if (!window.confirm(
      `Revoke "${conn.display_name}"? This soft-deletes the connection and pauses `
      + "any scheduled action bound to it. You can create a new connection any time.",
    )) return;
    try {
      await api.delete(`/publish/channel-connections/${conn.id}`);
      setFlash(`Revoked "${conn.display_name}".`);
      load();
    } catch (e) {
      setState((s) => ({ ...s, error: formatApiError(e) }));
    }
  }

  return (
    <Shell title="Admin · Channel Connections">
      <div className="bo-page cc-wrap" data-testid="channel-connections-page">
        {/* ---------- Header + intro ---------- */}
        <section className="bo-card" data-testid="channel-connections-header">
          <div className="bo-eyebrow">Publish Infrastructure</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 8px" }}>
            Channel Connections
          </h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 780, lineHeight: 1.55 }}>
            Provider-neutral bindings between Brand OS and the destinations you publish to
            (a specific Facebook Page, a Resend sending account, etc.). Every Publish Plan
            and Publish Receipt references a <code>channel_connection_id</code>, never a raw
            token. Secrets are Fernet-encrypted at rest and never surface again once saved.
          </p>
          <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="connect-oauth-fb"
              onClick={handleStartOAuth}
              disabled={isBusy("facebook")}
              title="Connects your Facebook Page and automatically links the Instagram Business/Creator account tied to it."
            >
              {isBusy("facebook") ? "Redirecting…" : "Connect Facebook + Instagram (OAuth)"}
            </button>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="connect-oauth-yt"
              onClick={handleStartYouTubeOAuth}
              disabled={isBusy("youtube")}
              title="Connects a YouTube Brand Account channel (@PrecipiceHealth). Each plan's privacy (public / unlisted / private) comes from its channel_config; scheduled posts default to public."
            >
              {isBusy("youtube") ? "Redirecting…" : "Connect YouTube (OAuth)"}
            </button>
            <button
              className="bo-btn bo-btn-primary"
              data-testid="connect-oauth-tt"
              onClick={handleStartTikTokOAuth}
              disabled={isBusy("tiktok")}
              title={ttProfile?.connect_blocked
                ? ttProfile.message
                : "Connects this business's TikTok account. With video.publish the Post to TikTok page publishes directly; otherwise videos land in the TikTok inbox as drafts."}
            >
              {isBusy("tiktok") ? "Redirecting…" : "Connect TikTok (OAuth)"}
            </button>
            {ttProfile?.sandbox_mode && (
              <span
                data-testid="tiktok-credential-profile"
                title={ttProfile.message || `TikTok credential profile: ${ttProfile.credential_profile}`}
                style={{
                  alignSelf: "center", fontSize: 12, padding: "4px 10px", borderRadius: 999,
                  border: "1px solid var(--bo-line)",
                  color: ttProfile.connect_blocked ? "var(--bo-warn)" : "var(--bo-success)",
                }}
              >
                {ttProfile.connect_blocked
                  ? "TikTok sandbox credentials missing — fill them in Secrets"
                  : "TikTok sandbox mode (preview)"}
              </span>
            )}
            <button
              className="bo-btn bo-btn-primary"
              data-testid="connect-oauth-pn"
              onClick={handleStartPinterestOAuth}
              disabled={isBusy("pinterest")}
              title="Connects a Pinterest business account. Trial access caps rate limits until Standard access is granted."
            >
              {isBusy("pinterest") ? "Redirecting…" : "Connect Pinterest (OAuth)"}
            </button>
            <button
              className="bo-btn bo-btn-ghost"
              data-testid="connect-add-manual"
              onClick={() => setModal({ kind: "create" })}
            >
              + Add DEV_MANUAL connection
            </button>
            <label
              style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--bo-muted)" }}
            >
              <input
                type="checkbox"
                data-testid="toggle-include-revoked"
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
              Show revoked
            </label>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--bo-muted)", lineHeight: 1.55 }}>
            One consent screen covers both channels — no tokens are ever pasted.
            After you authorize the Facebook Page, we auto-discover the
            Instagram Business/Creator account linked to it and create a
            second connection using the same Page token.
          </div>
          {flash && (
            <div
              className="bo-flash"
              data-testid="channel-connections-flash"
              style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)", color: "var(--bo-ink)", fontSize: 13 }}
            >
              {flash}
            </div>
          )}
        </section>

        {/* ---------- Phase-0 Task 3: red banner when default is revoked/expired ---------- */}
        {fleet?.banner?.show && (
          <div
            data-testid="channel-connections-expiry-banner"
            style={{
              marginTop: 14, padding: "14px 18px", borderRadius: 10,
              background: "#fde8e4", border: "1px solid #d9826c",
              color: "#7a2b1a", fontSize: 14, lineHeight: 1.5,
              display: "flex", gap: 12, alignItems: "flex-start",
            }}
          >
            <div style={{ fontSize: 18, lineHeight: 1 }}>⚠</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                Default connection needs attention
              </div>
              <div>{fleet.banner.reason} Reconnect below to resume publishing.</div>
            </div>
          </div>
        )}

        {/* ---------- List ---------- */}
        <section style={{ marginTop: 20 }}>
          <ErrorBanner error={state.error} testId="channel-connections-error" />
          {state.loading ? (
            <LoadingCard label="Loading connections…" />
          ) : connections.length === 0 ? (
            <EmptyState
              testId="channel-connections-empty"
              title="No channel connections yet"
              description={"Add a DEV_MANUAL connection to publish to Facebook, Resend, or another registered channel. "
                + "OAuth-managed connections arrive in Gate 5.3."}
            />
          ) : (
            <div style={{ display: "grid", gap: 12 }} data-testid="channel-connections-list">
              {connections.map((c) => (
                <ConnectionCard
                  key={c.id}
                  connection={c}
                  fleetRow={(fleet?.connections || []).find((f) => f.id === c.id) || null}
                  approval={approvals.find((a) => a.channel === c.channel) || null}
                  onRotate={() => setModal({ kind: "rotate", connection: c })}
                  onRevoke={() => handleRevoke(c)}
                  onReconnect={() => handleReconnect(c)}
                  oauthBusy={isBusy(`reconnect:${c.id}`)}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </section>

        {/* ---------- Phase 11D · Instagram Go-Live card ---------- */}
        <InstagramGoLiveCard flashSetter={setFlash} />

        {/* ---------- Modal ---------- */}
        {modal && (
          <div
            className="bo-modal-backdrop"
            data-testid="channel-connections-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.currentTarget === e.target) setModal(null); }}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(26, 26, 46, 0.35)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 100,
            }}
          >
            <div
              className="bo-card"
              style={{ width: "min(540px, 92vw)", background: "var(--bo-panel)", boxShadow: "var(--bo-shadow-lg)" }}
            >
              <div className="bo-eyebrow">
                {modal.kind === "create" ? "New connection" : "Rotate secret"}
              </div>
              <h2 className="bo-serif" style={{ fontSize: 20, margin: "6px 0 14px" }}>
                {modal.kind === "create"
                  ? "Add a DEV_MANUAL connection"
                  : `Rotate: ${modal.connection.display_name}`}
              </h2>
              {modal.kind === "create" ? (
                <CreateConnectionForm
                  onCancel={() => setModal(null)}
                  onCreated={(row) => {
                    setModal(null);
                    setFlash(`Created "${row.display_name}" (fp ${row.secret_fingerprint}).`);
                    load();
                  }}
                />
              ) : (
                <RotateSecretForm
                  connection={modal.connection}
                  onCancel={() => setModal(null)}
                  onRotated={(row) => {
                    setModal(null);
                    setFlash(`Rotated "${row.display_name}" → new fp ${row.secret_fingerprint}.`);
                    load();
                  }}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Shell>
  );
}

/* -------------------------------------------------------------------------- */
/*  TikTok · Review-demo test upload card                                     */
/* -------------------------------------------------------------------------- */
//
// Lets an owner send one short MP4/MOV to the connected TikTok account's
// Inbox as a draft. The card is intentionally scoped to review flows —
// the Simple Campaign board does not attach video to TikTok posts yet.
//
// The server caps the file at 50 MB and rate-limits to 5 attempts / hour.
// Copy here MUST say "TikTok inbox draft", not "posted"; the operator
// finishes publishing from inside the TikTok app.
//
function TikTokTestUploadCard({ connectionId }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [status, setStatus] = useState({ kind: "idle" }); // idle | uploading | success | error
  const [tiktokErr, setTiktokErr] = useState(null);
  const inputRef = React.useRef(null);
  const MAX_BYTES = 50 * 1024 * 1024;

  function pick(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ok = ["video/mp4", "video/quicktime"].includes((f.type || "").toLowerCase());
    if (!ok) {
      setStatus({ kind: "error", message: "Only MP4 (.mp4) or MOV (.mov) files are allowed." });
      setFile(null);
      return;
    }
    if (f.size > MAX_BYTES) {
      setStatus({
        kind: "error",
        message: `File is ${(f.size / 1024 / 1024).toFixed(1)} MB; max is 50 MB.`,
      });
      setFile(null);
      return;
    }
    setStatus({ kind: "idle" });
    setTiktokErr(null);
    setFile(f);
  }

  async function send() {
    if (!file) return;
    setStatus({ kind: "uploading", progressPct: null });
    setTiktokErr(null);
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption.slice(0, 300));
    try {
      const r = await api.post(
        "/publish/tiktok/test-inbox-upload",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setStatus({
        kind: "success",
        publishId: r.data?.publish_id,
        message: r.data?.ui_message || "Sent to your TikTok inbox — open TikTok to finish posting.",
        testMode: !!r.data?.test_mode,
      });
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setTiktokErr({
        reason: d.reason || "upload_failed",
        message: d.message || formatApiError(e),
        tiktok: d.tiktok || null,
        httpStatus: e?.response?.status,
      });
      setStatus({ kind: "error", message: d.message || formatApiError(e) });
    }
  }

  function reset() {
    setFile(null);
    setCaption("");
    setStatus({ kind: "idle" });
    setTiktokErr(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      data-testid={`tiktok-test-upload-card-${connectionId}`}
      style={{
        marginTop: 14,
        padding: 14,
        border: "1px dashed var(--bo-line)",
        borderRadius: 8,
        background: "var(--bo-panel-alt, #faf8f2)",
      }}
    >
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>
        Review tool · Send a test video to TikTok inbox
      </div>
      <p style={{ marginTop: 6, marginBottom: 10, fontSize: 13, color: "var(--bo-muted)", lineHeight: 1.5 }}>
        Uploads the file as a <strong>TikTok inbox draft</strong>. Nothing goes live until you open the TikTok app and tap Post. MP4 or MOV, up to 50 MB. 5 uploads per hour.
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          onChange={pick}
          disabled={status.kind === "uploading"}
          data-testid={`tiktok-test-upload-file-${connectionId}`}
          style={{ fontSize: 13, flex: "1 1 220px", minWidth: 0 }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label
          htmlFor={`tt-caption-${connectionId}`}
          style={{ fontSize: 12, color: "var(--bo-muted)", display: "block", marginBottom: 4 }}
        >
          Caption (for your reference — set the TikTok caption inside the app at publish time)
        </label>
        <input
          id={`tt-caption-${connectionId}`}
          className="bo-input"
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="e.g. test clip for TikTok review"
          maxLength={300}
          disabled={status.kind === "uploading"}
          data-testid={`tiktok-test-upload-caption-${connectionId}`}
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        {file && (
          <span
            style={{ fontSize: 12, color: "var(--bo-muted)", marginRight: "auto" }}
            data-testid={`tiktok-test-upload-filename-${connectionId}`}
          >
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
          </span>
        )}
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={reset}
          disabled={status.kind === "uploading"}
          data-testid={`tiktok-test-upload-reset-${connectionId}`}
        >
          Clear
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={send}
          disabled={!file || status.kind === "uploading"}
          data-testid={`tiktok-test-upload-send-${connectionId}`}
        >
          {status.kind === "uploading" ? "Uploading…" : "Send to TikTok inbox"}
        </button>
      </div>

      {status.kind === "uploading" && (
        <div
          style={{ marginTop: 10, fontSize: 13, color: "var(--bo-ink)" }}
          data-testid={`tiktok-test-upload-progress-${connectionId}`}
        >
          Uploading to TikTok…
        </div>
      )}

      {status.kind === "success" && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            border: "1px solid var(--bo-ok, #2e7d32)",
            background: "rgba(46, 125, 50, 0.06)",
            color: "var(--bo-ok, #2e7d32)",
            borderRadius: 6,
            fontSize: 13,
            lineHeight: 1.5,
          }}
          data-testid={`tiktok-test-upload-success-${connectionId}`}
        >
          <strong>{status.message}</strong>
          <br />
          <span style={{ color: "var(--bo-muted)", fontSize: 12 }}>
            publish_id: <code>{status.publishId}</code>
            {status.testMode ? " · test mode (no real upload sent)" : ""}
          </span>
        </div>
      )}

      {status.kind === "error" && (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            border: "1px solid var(--bo-danger, #b4332a)",
            background: "rgba(180, 51, 42, 0.06)",
            color: "var(--bo-danger, #b4332a)",
            borderRadius: 6,
            fontSize: 13,
            lineHeight: 1.5,
          }}
          data-testid={`tiktok-test-upload-error-${connectionId}`}
        >
          <strong>Upload failed.</strong> {status.message}
          {tiktokErr && tiktokErr.tiktok && (tiktokErr.tiktok.code || tiktokErr.tiktok.message) && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)" }}>
              TikTok error: <code>{tiktokErr.tiktok.code || "—"}</code>
              {tiktokErr.tiktok.message ? ` — ${tiktokErr.tiktok.message}` : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------
// YouTube · "Upload a test video (private)" card
// --------------------------------------------------------------------
// Mirrors `TikTokTestUploadCard`: review-only. Uploads an MP4/MOV (<= 100 MB) via the
// backend's resumable `videos.insert` wrapper. Privacy is HARD-CODED
// to `private` — the UI has no override. On success renders the video
// id + a "View on YouTube" link + the upload timestamp.
const YOUTUBE_UPLOAD_SCOPE = "https://www.googleapis.com/auth/youtube.upload";

function YouTubeTestUploadCard({ connectionId, grantedScopes }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(
    "Precipice Health · Brand OS test upload",
  );
  const [description, setDescription] = useState(
    "Review-only upload from Brand OS. Verifies the youtube.upload "
    + "scope is wired end-to-end.",
  );
  const [status, setStatus] = useState({ kind: "idle" });
  const [ytErr, setYtErr] = useState(null);
  const inputRef = React.useRef(null);
  const MAX_BYTES = 100 * 1024 * 1024;
  const hasUploadScope = (grantedScopes || []).includes(YOUTUBE_UPLOAD_SCOPE);

  function pick(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const ok = ["video/mp4", "video/quicktime"].includes(
      (f.type || "").toLowerCase(),
    );
    if (!ok) {
      setStatus({ kind: "error", message: "Only MP4 (.mp4) or MOV (.mov) files are allowed." });
      setFile(null); return;
    }
    if (f.size > MAX_BYTES) {
      setStatus({
        kind: "error",
        message: `File is ${(f.size / 1024 / 1024).toFixed(1)} MB; max is 100 MB.`,
      });
      setFile(null); return;
    }
    setStatus({ kind: "idle" });
    setYtErr(null);
    setFile(f);
  }

  async function send() {
    if (!file) return;
    setStatus({ kind: "uploading" });
    setYtErr(null);
    const form = new FormData();
    form.append("file", file);
    form.append("title", title.slice(0, 200));
    form.append("description", description.slice(0, 5000));
    try {
      const r = await api.post(
        "/publish/youtube/test-upload",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setStatus({
        kind: "success",
        videoId: r.data?.video_id,
        youtubeUrl: r.data?.youtube_url,
        uploadedAt: r.data?.uploaded_at,
        testMode: !!r.data?.test_mode,
        message: r.data?.ui_message
          || "Uploaded to YouTube as private.",
      });
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setYtErr({
        reason: d.reason || "upload_failed",
        message: d.message || formatApiError(e),
        httpStatus: e?.response?.status,
      });
      setStatus({ kind: "error", message: d.message || formatApiError(e) });
    }
  }

  function reset() {
    setFile(null);
    setStatus({ kind: "idle" });
    setYtErr(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div
      data-testid={`youtube-test-upload-card-${connectionId}`}
      style={{
        marginTop: 14,
        padding: 14,
        border: "1px dashed var(--bo-line)",
        borderRadius: 8,
        background: "var(--bo-panel-alt, #faf8f2)",
      }}
    >
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>
        Review tool · Upload a test video to YouTube (private)
      </div>
      <p
        style={{
          marginTop: 6, marginBottom: 10, fontSize: 13,
          color: "var(--bo-muted)", lineHeight: 1.5,
        }}
      >
        Uploads an MP4 or MOV directly to the connected YouTube channel
        as a <strong>private</strong> video. Only the channel owner can
        see it on YouTube. Nothing goes public. MP4 or MOV, up to 100 MB.
        5 uploads per hour.
      </p>

      {!hasUploadScope && (
        <div
          data-testid={`youtube-test-upload-scope-warning-${connectionId}`}
          style={{
            marginBottom: 10, padding: "8px 10px",
            border: "1px solid var(--bo-danger, #b4332a)",
            background: "rgba(180, 51, 42, 0.06)",
            color: "var(--bo-danger, #b4332a)",
            borderRadius: 6, fontSize: 12, lineHeight: 1.5,
          }}
        >
          This connection does not have the upload scope. Click
          <strong> Revoke </strong> above, then <strong> Connect
          YouTube </strong> again and tick the Upload permission.
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,.mp4,.mov"
          onChange={pick}
          disabled={status.kind === "uploading" || !hasUploadScope}
          data-testid={`youtube-test-upload-file-${connectionId}`}
          style={{ fontSize: 13, flex: "1 1 220px", minWidth: 0 }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label
          htmlFor={`yt-title-${connectionId}`}
          style={{ fontSize: 12, color: "var(--bo-muted)", display: "block", marginBottom: 4 }}
        >
          Title (shown on YouTube · 200 char max)
        </label>
        <input
          id={`yt-title-${connectionId}`}
          className="bo-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          disabled={status.kind === "uploading" || !hasUploadScope}
          data-testid={`youtube-test-upload-title-${connectionId}`}
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label
          htmlFor={`yt-desc-${connectionId}`}
          style={{ fontSize: 12, color: "var(--bo-muted)", display: "block", marginBottom: 4 }}
        >
          Description (shown on YouTube · 5000 char max)
        </label>
        <textarea
          id={`yt-desc-${connectionId}`}
          className="bo-input"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={5000}
          disabled={status.kind === "uploading" || !hasUploadScope}
          data-testid={`youtube-test-upload-description-${connectionId}`}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
      </div>

      <div
        style={{
          marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap",
          justifyContent: "flex-end", alignItems: "center",
        }}
      >
        {file && (
          <span
            style={{ fontSize: 12, color: "var(--bo-muted)", marginRight: "auto" }}
            data-testid={`youtube-test-upload-filename-${connectionId}`}
          >
            {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB · privacy locked to private
          </span>
        )}
        <button
          type="button"
          className="bo-btn bo-btn-ghost"
          onClick={reset}
          disabled={status.kind === "uploading"}
          data-testid={`youtube-test-upload-reset-${connectionId}`}
        >
          Clear
        </button>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={send}
          disabled={!file || status.kind === "uploading" || !hasUploadScope}
          data-testid={`youtube-test-upload-send-${connectionId}`}
        >
          {status.kind === "uploading" ? "Uploading…" : "Upload to YouTube (private)"}
        </button>
      </div>

      {status.kind === "uploading" && (
        <div
          style={{ marginTop: 10, fontSize: 13, color: "var(--bo-ink)" }}
          data-testid={`youtube-test-upload-progress-${connectionId}`}
        >
          Uploading to YouTube…
        </div>
      )}

      {status.kind === "success" && (
        <div
          style={{
            marginTop: 10, padding: "10px 12px",
            border: "1px solid var(--bo-ok, #2e7d32)",
            background: "rgba(46, 125, 50, 0.06)",
            color: "var(--bo-ok, #2e7d32)",
            borderRadius: 6, fontSize: 13, lineHeight: 1.5,
          }}
          data-testid={`youtube-test-upload-success-${connectionId}`}
        >
          <strong>{status.message}</strong>
          <br />
          <span style={{ color: "var(--bo-muted)", fontSize: 12 }}>
            video_id: <code data-testid={`youtube-test-upload-video-id-${connectionId}`}>{status.videoId}</code>
            {status.testMode ? " · test mode (no real upload sent)" : ""}
            {status.uploadedAt ? ` · uploaded ${new Date(status.uploadedAt).toLocaleString()}` : ""}
          </span>
          {status.youtubeUrl && (
            <div style={{ marginTop: 6 }}>
              <a
                href={status.youtubeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`youtube-test-upload-watch-link-${connectionId}`}
                style={{ color: "var(--bo-ok, #2e7d32)", textDecoration: "underline" }}
              >
                View on YouTube →
              </a>
            </div>
          )}
        </div>
      )}

      {status.kind === "error" && (
        <div
          style={{
            marginTop: 10, padding: "10px 12px",
            border: "1px solid var(--bo-danger, #b4332a)",
            background: "rgba(180, 51, 42, 0.06)",
            color: "var(--bo-danger, #b4332a)",
            borderRadius: 6, fontSize: 13, lineHeight: 1.5,
          }}
          data-testid={`youtube-test-upload-error-${connectionId}`}
        >
          <strong>Upload failed.</strong> {status.message}
          {ytErr && ytErr.reason && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)" }}>
              reason: <code>{ytErr.reason}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// --------------------------------------------------------------------
// Pinterest · "Send a test Pin" card (Standard-access upgrade demo)
// --------------------------------------------------------------------
// Mirrors `TikTokTestUploadCard`. Lists the boards the connection can
// write to, lets the operator pick one, enter title/description/link,
// and POSTs the Pin to `/publish/pinterest/send-test-pin`. Honors
// `PINTEREST_API_BASE=https://api-sandbox.pinterest.com` on the backend
// so trial-tier apps automatically hit the sandbox (visible only to
// the operator's Pinterest account).
function PinterestTestPinCard({ connectionId }) {
  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [altText, setAltText] = useState("");
  const [loadingBoards, setLoadingBoards] = useState(false);
  const [status, setStatus] = useState({ kind: "idle" });
  const [apiBase, setApiBase] = useState(null);
  const [sandbox, setSandbox] = useState(null);

  const loadBoards = useCallback(async () => {
    setLoadingBoards(true);
    try {
      const r = await api.get("/publish/pinterest/boards");
      setBoards(r.data?.boards || []);
      setApiBase(r.data?.api_base || null);
      setSandbox(!!r.data?.sandbox);
      // Prefer any board whose name contains "Brand OS test" to keep
      // the demo Pins off real customer boards.
      const demo = (r.data?.boards || []).find(
        (b) => /brand\s*os\s*test/i.test(b.name || ""),
      );
      if (demo) setBoardId(demo.id);
      else if ((r.data?.boards || []).length) setBoardId(r.data.boards[0].id);
    } catch (e) {
      setStatus({ kind: "error",
                  message: `Could not load boards: ${formatApiError(e)}` });
    } finally {
      setLoadingBoards(false);
    }
  }, []);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  async function send() {
    if (!boardId) {
      setStatus({ kind: "error", message: "Pick a board." });
      return;
    }
    if (!title.trim()) {
      setStatus({ kind: "error", message: "Title is required." });
      return;
    }
    if (!imageUrl.trim()) {
      setStatus({ kind: "error",
                  message: "Public image URL is required for the Pin." });
      return;
    }
    setStatus({ kind: "sending" });
    try {
      const r = await api.post("/publish/pinterest/send-test-pin", {
        board_id: boardId,
        image_url: imageUrl.trim(),
        title: title.trim(),
        description: description.trim() || null,
        destination_link: link.trim() || null,
        alt_text: altText.trim() || null,
      });
      setStatus({
        kind: "success",
        pinId: r.data?.pinterest_pin_id,
        permalink: r.data?.permalink_url,
        sandbox: !!r.data?.sandbox,
        message: r.data?.ui_message || "Pin published.",
      });
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      setStatus({
        kind: "error",
        message: d.message || formatApiError(e),
        pinterestError: d.pinterest || null,
      });
    }
  }

  function reset() {
    setStatus({ kind: "idle" });
    setTitle("");
    setDescription("");
    setLink("");
    setAltText("");
    setImageUrl("");
  }

  return (
    <div
      data-testid={`pinterest-test-pin-card-${connectionId}`}
      style={{
        marginTop: 14,
        padding: 14,
        border: "1px dashed var(--bo-line)",
        borderRadius: 8,
        background: "var(--bo-panel-alt, #faf8f2)",
      }}
    >
      <div className="bo-eyebrow" style={{ fontSize: 10 }}>
        Review tool · Send a test Pin
      </div>
      <p style={{ marginTop: 6, marginBottom: 10, fontSize: 13,
                  color: "var(--bo-muted)", lineHeight: 1.5 }}>
        Posts an image Pin to the chosen board using your Pinterest
        connection. Keep a board called{" "}
        <strong>Brand OS test</strong> for the review demo so test Pins
        never mix with real ones. Image URL must be publicly reachable
        (2:3 vertical works best).
      </p>

      {apiBase && (
        <div style={{ fontSize: 11, marginBottom: 10,
                      color: sandbox ? "#8a5a0a" : "var(--bo-muted)" }}
             data-testid={`pinterest-test-pin-base-${connectionId}`}>
          API base: <code>{apiBase}</code>
          {sandbox && " · trial sandbox (Pins visible only to your account)"}
        </div>
      )}

      <div style={{ display: "grid", gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div>
          <label style={{ fontSize: 12, color: "var(--bo-muted)",
                          display: "block", marginBottom: 4 }}>
            Board
          </label>
          <select
            className="bo-input"
            value={boardId}
            onChange={(e) => setBoardId(e.target.value)}
            disabled={loadingBoards || status.kind === "sending"}
            data-testid={`pinterest-test-pin-board-${connectionId}`}
            style={{ width: "100%" }}
          >
            <option value="">{loadingBoards ? "Loading…" : "Pick a board"}</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <button type="button" className="bo-btn-link"
                  onClick={loadBoards}
                  disabled={loadingBoards}
                  data-testid={`pinterest-test-pin-reload-${connectionId}`}
                  style={{ fontSize: 11, marginTop: 2 }}>
            Reload boards
          </button>
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--bo-muted)",
                          display: "block", marginBottom: 4 }}>
            Image URL (public, 2:3)
          </label>
          <input
            className="bo-input"
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://…/image.jpg"
            disabled={status.kind === "sending"}
            data-testid={`pinterest-test-pin-image-${connectionId}`}
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 12, color: "var(--bo-muted)",
                        display: "block", marginBottom: 4 }}>
          Title ({title.length}/100)
        </label>
        <input
          className="bo-input"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
          disabled={status.kind === "sending"}
          data-testid={`pinterest-test-pin-title-${connectionId}`}
          style={{ width: "100%", boxSizing: "border-box" }}
        />
      </div>

      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 12, color: "var(--bo-muted)",
                        display: "block", marginBottom: 4 }}>
          Description ({description.length}/500)
        </label>
        <textarea
          className="bo-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          rows={2}
          disabled={status.kind === "sending"}
          data-testid={`pinterest-test-pin-description-${connectionId}`}
          style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
        />
      </div>

      <div style={{ marginTop: 10, display: "grid", gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <div>
          <label style={{ fontSize: 12, color: "var(--bo-muted)",
                          display: "block", marginBottom: 4 }}>
            Destination link (optional)
          </label>
          <input
            className="bo-input"
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://precipice-health.com/…"
            disabled={status.kind === "sending"}
            data-testid={`pinterest-test-pin-link-${connectionId}`}
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
        <div>
          <label style={{ fontSize: 12, color: "var(--bo-muted)",
                          display: "block", marginBottom: 4 }}>
            Alt text (optional, &lt; 500)
          </label>
          <input
            className="bo-input"
            type="text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            maxLength={500}
            disabled={status.kind === "sending"}
            data-testid={`pinterest-test-pin-alt-${connectionId}`}
            style={{ width: "100%", boxSizing: "border-box" }}
          />
        </div>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          className="bo-btn bo-btn-primary"
          onClick={send}
          disabled={status.kind === "sending" || !boardId
                    || !title.trim() || !imageUrl.trim()}
          data-testid={`pinterest-test-pin-send-${connectionId}`}
        >
          {status.kind === "sending" ? "Sending…" : "Send test Pin"}
        </button>
        <button
          type="button"
          className="bo-btn"
          onClick={reset}
          disabled={status.kind === "sending"}
          data-testid={`pinterest-test-pin-reset-${connectionId}`}
        >
          Reset
        </button>
      </div>

      {status.kind === "success" && (
        <div style={{ marginTop: 10, padding: 10,
                      borderRadius: 6, background: "#e6f4ea",
                      color: "#1f6c3f" }}
             data-testid={`pinterest-test-pin-success-${connectionId}`}>
          <strong>{status.sandbox ? "Sandbox Pin created." : "Pin published."}</strong>{" "}
          {status.message}
          {status.permalink && (
            <> ·{" "}
              <a href={status.permalink} target="_blank" rel="noreferrer"
                 data-testid={`pinterest-test-pin-link-${connectionId}`}>
                View on Pinterest
              </a>
            </>
          )}
        </div>
      )}
      {status.kind === "error" && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 6,
                      background: "#fee2d2", color: "#8a2b0a" }}
             role="alert"
             data-testid={`pinterest-test-pin-error-${connectionId}`}>
          <strong>Pin failed.</strong> {status.message}
          {status.pinterestError && (
            <div style={{ marginTop: 6, fontSize: 12, color: "var(--bo-muted)" }}>
              Pinterest: <code>{JSON.stringify(status.pinterestError).slice(0, 240)}</code>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



/* -------------------------------------------------------------------------- */
/*  Connection card                                                           */
/* -------------------------------------------------------------------------- */

function ConnectionCard({ connection, fleetRow, approval, onRotate, onRevoke, onReconnect, oauthBusy, onChanged }) {
  const revoked = connection.status === "REVOKED";
  const isFacebook = connection.channel === "facebook";
  const isYouTube = connection.channel === "youtube";
  const isTikTok = connection.channel === "tiktok";
  const isPinterest = connection.channel === "pinterest";
  const isOAuthManaged = isFacebook || isYouTube || isTikTok || isPinterest;
  const needsReauth = connection.health === "REAUTH_REQUIRED"
    || connection.health === "EXPIRING_SOON";
  // Phase-0 Task 3: expiry chip. Updated 2026-10 Safety-by-Default fix:
  // OAuth-managed connections that hold a refresh token mint fresh
  // access tokens on every publish — the raw access-token expiry is
  // not the connection's expiry. Backend now returns
  // `renews_automatically=true` for those rows and we render a green
  // pill instead of the misleading "expired (0d ago)".
  const days = fleetRow?.days_until_expiry;
  const expiresAt = fleetRow?.expires_at;
  const renewsAutomatically = Boolean(fleetRow?.renews_automatically);
  let expiryChip = null;
  if (renewsAutomatically) {
    expiryChip = (
      <Pill tone="success" testId={`connection-expiry-${connection.id}`}>
        renews automatically
      </Pill>
    );
  } else if (expiresAt) {
    let tone = "muted", label;
    if (days === null || days === undefined) {
      label = `exp ${String(expiresAt).slice(0, 10)}`;
    } else if (days <= 0) {
      tone = "danger"; label = `expired (${-days}d ago)`;
    } else if (days <= 10) {
      tone = "warn"; label = `expires in ${days}d`;
    } else {
      label = `expires in ${days}d`;
    }
    expiryChip = (
      <Pill tone={tone} testId={`connection-expiry-${connection.id}`}>
        {label}
      </Pill>
    );
  }
  return (
    <div
      className="bo-card"
      data-testid={`connection-card-${connection.id}`}
      style={revoked ? { opacity: 0.65 } : undefined}
    >
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 320px", minWidth: 0, overflowWrap: "anywhere" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
            <span style={{ fontFamily: "var(--bo-serif)", fontSize: 17, color: "var(--bo-ink)", overflowWrap: "anywhere" }}>
              {connection.display_name}
            </span>
            {connection.is_default && !revoked && (
              <Pill tone="success" testId={`connection-default-${connection.id}`}>default</Pill>
            )}
            {!revoked && (
              <LiveModeToggle connection={connection} onChanged={onChanged} />
            )}
            {!revoked && (
              <PinterestDefaultBoardChip
                connection={connection} onChanged={onChanged}
              />
            )}
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: "var(--bo-muted)" }}>
            <span>{CHANNEL_LABEL[connection.channel] || connection.channel}</span>
            <span style={{ margin: "0 8px" }}>·</span>
            <span data-testid={`connection-type-${connection.id}`}>{connection.connection_type}</span>
          </div>
          {connection.provider_ref?.page_id && (
            <div className="bo-mono" style={{ marginTop: 4, fontSize: 12, overflowWrap: "anywhere" }} data-testid={`connection-page-id-${connection.id}`}>
              Page ID: {connection.provider_ref.page_id}
            </div>
          )}
          {isTikTok && (connection.provider_ref?.display_name || connection.provider_ref?.username || connection.provider_ref?.avatar_url) && (
            <div
              style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}
              data-testid={`connection-tiktok-identity-${connection.id}`}
            >
              {connection.provider_ref.avatar_url ? (
                <img
                  src={connection.provider_ref.avatar_url}
                  alt=""
                  width={36}
                  height={36}
                  style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid var(--bo-line)" }}
                  data-testid={`connection-tiktok-avatar-${connection.id}`}
                />
              ) : (
                <div
                  aria-hidden="true"
                  style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--bo-panel-alt, #faf8f2)", border: "1px solid var(--bo-line)", flexShrink: 0 }}
                />
              )}
              <div style={{ lineHeight: 1.25, minWidth: 0 }}>
                {connection.provider_ref.display_name && (
                  <div
                    style={{ fontSize: 13, color: "var(--bo-ink)", fontWeight: 600, overflowWrap: "anywhere" }}
                    data-testid={`connection-tiktok-display-name-${connection.id}`}
                  >
                    {connection.provider_ref.display_name}
                  </div>
                )}
                {connection.provider_ref.username && (
                  <div
                    style={{ fontSize: 12, color: "var(--bo-muted)" }}
                    data-testid={`connection-tiktok-username-${connection.id}`}
                  >
                    @{connection.provider_ref.username}
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="bo-mono" style={{ marginTop: 4, fontSize: 12, color: "var(--bo-muted)", overflowWrap: "anywhere" }} data-testid={`connection-fingerprint-${connection.id}`}>
            fp {connection.secret_fingerprint || "—"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 6, flexDirection: "column", alignItems: "flex-end", flex: "0 1 300px", minWidth: 0, maxWidth: "100%", overflowWrap: "anywhere" }}>
          <Pill
            tone={STATUS_TONE[connection.status] || "muted"}
            testId={`connection-status-${connection.id}`}
          >
            {connection.status}
          </Pill>
          <Pill
            tone={HEALTH_TONE[connection.health] || "muted"}
            testId={`connection-health-${connection.id}`}
          >
            {connection.health || "UNKNOWN"}
          </Pill>
          {expiryChip}
          {connection.channel === "pinterest" && approval
            && approval.last_api_evidence
            && (() => {
              const tier = (approval.last_api_evidence.access_tier || "trial").toLowerCase();
              const isStd = tier === "standard" || tier === "production";
              return (
                <Pill
                  tone={isStd ? "ok" : "warn"}
                  testId={`connection-pinterest-tier-${connection.id}`}
                >
                  {isStd ? "Standard access" : "Trial access"}
                </Pill>
              );
            })()}
          {isYouTube && !revoked && (() => {
            // Round 15-B (2026-10-30): Google's YouTube API team confirmed
            // API uploads are NOT auto-privated and closed the audit, so the
            // old "AUDIT_PENDING · private" badge is replaced by the actual
            // default privacy for scheduled posts (per-plan override via
            // channel_config.privacy_status: public / unlisted / private).
            // Only an OWNER-set private floor changes the default; probe
            // states like "revoked" (no active connection yet) do not.
            const defaultPrivacy = (approval && approval.baseline_source === "owner"
              && approval.effective_privacy && approval.effective_privacy !== "public")
              ? approval.effective_privacy.replace(/_/g, " ")
              : "public";
            return (
              <>
                <Pill
                  tone={defaultPrivacy === "public" ? "success" : "warn"}
                  testId={`connection-youtube-default-privacy-${connection.id}`}
                >
                  Default privacy · {defaultPrivacy}
                </Pill>
                <div
                  style={{ marginTop: 2, maxWidth: "min(260px, 100%)", fontSize: 11,
                            color: "var(--bo-muted)", textAlign: "right",
                            lineHeight: 1.4, overflowWrap: "anywhere" }}
                  data-testid={`connection-youtube-privacy-note-${connection.id}`}
                >
                  Scheduled YouTube posts publish as <strong>{defaultPrivacy}</strong> unless a
                  plan sets <code>privacy_status</code> (public / unlisted / private).
                  Acceptance and review-tool uploads stay private.
                </div>
              </>
            );
          })()}
          {!isYouTube && approval && approval.requires_private_post && (
            <Pill
              tone="warn"
              testId={`connection-approval-${connection.id}`}
            >
              {(approval.status || "unknown").toUpperCase()} · {(approval.effective_privacy || "private").replace(/_/g, " ")}
            </Pill>
          )}
          {!isYouTube && approval && approval.requires_private_post && (
            <div
              style={{ marginTop: 2, maxWidth: "min(260px, 100%)", fontSize: 11,
                        color: "var(--bo-muted)", textAlign: "right",
                        lineHeight: 1.4, overflowWrap: "anywhere" }}
              data-testid={`connection-approval-banner-${connection.id}`}
            >
              {approval.banner}
            </div>
          )}
          {approval && approval.info_note && (
            <div
              style={{ marginTop: 4, maxWidth: "min(300px, 100%)", fontSize: 11,
                        color: "var(--bo-muted)", textAlign: "right",
                        lineHeight: 1.4, fontStyle: "italic", overflowWrap: "anywhere" }}
              data-testid={`connection-approval-info-${connection.id}`}
            >
              ⓘ {approval.info_note}
            </div>
          )}
          {(HEALTH_EXPLAINER[connection.health || "UNKNOWN"]) && (
            <div
              style={{ marginTop: 2, maxWidth: "min(260px, 100%)", fontSize: 11, color: "var(--bo-muted)", textAlign: "right", lineHeight: 1.4, overflowWrap: "anywhere" }}
              data-testid={`connection-health-explainer-${connection.id}`}
            >
              {HEALTH_EXPLAINER[connection.health || "UNKNOWN"]}
            </div>
          )}
          {connection.health_reason && (
            <div
              style={{ marginTop: 4, maxWidth: "min(260px, 100%)", fontSize: 12, color: "var(--bo-danger)", textAlign: "right", lineHeight: 1.35, overflowWrap: "anywhere" }}
              title={connection.health_reason}
              data-testid={`connection-health-reason-${connection.id}`}
            >
              <strong>Last error:</strong> {connection.health_reason.length > 90
                ? connection.health_reason.slice(0, 90) + "…"
                : connection.health_reason}
            </div>
          )}
        </div>
      </div>

      <div
        style={{ marginTop: 14, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 8, fontSize: 12, color: "var(--bo-muted)" }}
      >
        <div>
          <div className="bo-eyebrow" style={{ fontSize: 10 }}>Created</div>
          <div data-testid={`connection-created-${connection.id}`}>{formatDateTime(connection.created_at)}</div>
        </div>
        <div>
          <div className="bo-eyebrow" style={{ fontSize: 10 }}>Last publish OK</div>
          <div data-testid={`connection-last-ok-${connection.id}`}>{formatRelative(connection.last_publish_ok_at)}</div>
        </div>
        <div>
          <div className="bo-eyebrow" style={{ fontSize: 10 }}>Last exercised</div>
          <div data-testid={`connection-last-used-${connection.id}`}>{formatRelative(connection.last_used_at)}</div>
        </div>
        <div>
          <div className="bo-eyebrow" style={{ fontSize: 10 }}>Scopes</div>
          <ScopeChips scopes={connection.scopes} testId={`connection-scopes-${connection.id}`} />
        </div>
      </div>

      {/* publish-85 round-9 · item D1 — YouTube delete needs youtube.force-ssl.
          Older connections were granted youtube.upload only; show exactly
          which scope is missing + an inline Reconnect to re-consent. */}
      {!revoked && isYouTube && (connection.missing_delete_scopes || []).length > 0 && (
        <div
          className="bo-card"
          data-testid={`connection-scope-hint-${connection.id}`}
          style={{ marginTop: 12, padding: "10px 12px", fontSize: 12,
                   background: "var(--bo-warn-bg, rgba(160,90,0,.06))",
                   border: "1px solid var(--bo-warn, #a05a00)",
                   display: "flex", gap: 12, alignItems: "center",
                   flexWrap: "wrap" }}
        >
          <div style={{ flex: "1 1 280px", lineHeight: 1.45 }}>
            <strong>Delete-from-platform needs a scope this connection doesn't have.</strong>
            <div style={{ color: "var(--bo-muted)", marginTop: 2 }}>
              Missing:{" "}
              <code
                data-testid={`connection-missing-scopes-${connection.id}`}
                title={connection.missing_delete_scopes.join(", ")}
              >
                {connection.missing_delete_scopes.map(shortScope).join(", ")}
              </code>
              . Publishing still works; deleting a YouTube video from Brand OS
              will fail with 403 until you reconnect and tick every Google
              permission.
            </div>
          </div>
          {onReconnect && (
            <button
              type="button"
              className="bo-btn bo-btn-primary"
              data-testid={`connection-scope-reconnect-${connection.id}`}
              onClick={onReconnect}
              disabled={oauthBusy}
              style={{ padding: "6px 12px", fontSize: 12 }}
            >
              {oauthBusy ? "Redirecting…" : "Reconnect to grant scope"}
            </button>
          )}
        </div>
      )}

      {!revoked && isTikTok && (
        <TikTokTestUploadCard connectionId={connection.id} />
      )}

      {!revoked && isYouTube && (
        <YouTubeTestUploadCard
          connectionId={connection.id}
          grantedScopes={connection.scopes || connection.granted_scopes || []}
        />
      )}

      {!revoked && isPinterest && (
        <PinterestTestPinCard connectionId={connection.id} />
      )}

      {!revoked && (
        <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {isOAuthManaged && onReconnect && (
            <button
              className="bo-btn bo-btn-ghost"
              data-testid={`connection-reconnect-${connection.id}`}
              onClick={onReconnect}
              disabled={oauthBusy}
              style={needsReauth ? { color: "var(--bo-accent)" } : undefined}
              title={needsReauth
                ? "This connection needs a fresh OAuth authorization"
                : `Refresh authorization via ${
                    isYouTube ? "Google" :
                    isTikTok ? "TikTok" :
                    isPinterest ? "Pinterest" : "Meta"
                  } OAuth`}
            >
              {needsReauth ? "Reconnect (OAuth)" : "Reconnect"}
            </button>
          )}
          <button
            className="bo-btn bo-btn-ghost"
            data-testid={`connection-rotate-${connection.id}`}
            onClick={onRotate}
          >
            Rotate secret
          </button>
          {isFacebook && !revoked && (
            <button
              className="bo-btn bo-btn-ghost"
              data-testid={`connection-discover-ig-${connection.id}`}
              onClick={async () => {
                try {
                  const r = await api.post(
                    `/publish/channel-connections/${connection.id}/discover-instagram`,
                  );
                  const msg = r.data?.discovered
                    ? `Instagram linked: @${r.data.ig_username}`
                    : (r.data?.message || "No Instagram account linked to this Page.");
                  window.alert(msg);
                } catch (e) {
                  window.alert(formatApiError(e));
                }
              }}
              title="Retry Instagram auto-discovery (useful if you linked the IG account after this Page connection was made)."
            >
              Retry Instagram discovery
            </button>
          )}
          <button
            className="bo-btn bo-btn-ghost"
            data-testid={`connection-revoke-${connection.id}`}
            onClick={onRevoke}
            style={{ color: "var(--bo-danger)" }}
          >
            Revoke
          </button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 11D · Instagram Go-Live card.
// Shows the preflight status (resolved @username, scopes, token expiry),
// and lets the operator reversibly flip the stored toggle. Even when ON,
// the adapter still requires the IG_PUBLISH_LIVE env flag before any call
// goes to Meta. Preview default is OFF.
// ---------------------------------------------------------------------------
function InstagramGoLiveCard({ flashSetter }) {
  const [state, setState] = useState({ loading: true, error: null });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const r = await api.get("/publish/instagram/go-live");
      setData(r.data);
      setState({ loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: formatApiError(e) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggle(nextEnabled, force = false) {
    setBusy(true);
    try {
      const r = await api.post("/publish/instagram/go-live", {
        enabled: nextEnabled, reason: reason || null, force,
      });
      setData(r.data);
      flashSetter?.(
        nextEnabled
          ? "Instagram live mode: ON · an env flag is still required before Meta is called."
          : "Instagram live mode: OFF · all publishes stay in TEST_MODE.",
      );
    } catch (e) {
      const msg = formatApiError(e);
      // If the server rejected because preflight failed, offer force.
      if (/preflight_not_ready/.test(String(e?.response?.data?.detail?.reason || ""))
          && nextEnabled) {
        if (window.confirm(
          "Instagram preflight did not pass. Enable anyway?\n\n"
          + "(The adapter still refuses to call Meta unless IG_PUBLISH_LIVE=1 "
          + "is set in the environment.)",
        )) {
          return toggle(true, true);
        }
      }
      setState((s) => ({ ...s, error: msg }));
    } finally {
      setBusy(false);
      load();
    }
  }

  return (
    <section
      className="bo-card"
      data-testid="instagram-go-live-card"
      style={{ marginTop: 20 }}
    >
      <div className="bo-eyebrow">Instagram · Go Live</div>
      <h2 className="bo-serif" style={{ fontSize: 22, margin: "6px 0 6px" }}>
        Instagram publishing
      </h2>
      <p style={{ color: "var(--bo-muted)", maxWidth: 760, lineHeight: 1.55 }}>
        Instagram shares the Facebook Page's token. Preview default is{" "}
        <strong>TEST_MODE</strong>. Turning this on flips a stored, reversible
        setting that you can see here. Even when it's on, the adapter still
        requires the <code>IG_PUBLISH_LIVE</code> environment flag before any
        call goes to Meta — a last-mile safety.
      </p>
      <ErrorBanner error={state.error} testId="instagram-go-live-error" />
      {state.loading && <LoadingCard label="Checking Instagram status…" />}
      {!state.loading && data && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <Pill tone={data.enabled ? "success" : "muted"}
                  data-testid="instagram-live-toggle-state">
              {data.enabled ? "LIVE ON (stored)" : "LIVE OFF (stored)"}
            </Pill>
            <Pill tone={data.env_live_enabled ? "success" : "muted"}>
              env flag · {data.env_live_enabled ? "IG_PUBLISH_LIVE=1" : "not set"}
            </Pill>
            <Pill tone={data.preflight?.ready ? "success" : "warn"}>
              preflight · {data.preflight?.ready ? "ready" : (data.preflight?.reason || "not ready")}
            </Pill>
          </div>
          <div style={{ marginTop: 12, fontSize: 13, color: "var(--bo-muted)", lineHeight: 1.6 }}>
            {data.preflight?.ig_username ? (
              <span data-testid="instagram-go-live-linked-account" style={{ overflowWrap: "anywhere" }}>
                Linked account: <strong>@{data.preflight.ig_username}</strong>{" "}
                (id <span className="bo-mono" style={{ fontSize: 12 }}>{data.preflight.ig_user_id}</span>)
              </span>
            ) : (
              <>No Instagram account resolved yet. Finish Facebook OAuth with a
                 Page that has an Instagram Business/Creator account linked, then
                 click <em>Retry Instagram discovery</em> on that connection below.</>
            )}
            {data.preflight?.expires_at && (
              <> · token expires {formatDateTime(data.preflight.expires_at)}</>
            )}
            {data.preflight?.scopes?.length ? (
              <div style={{ marginTop: 6, display: "flex", gap: 6, alignItems: "flex-start", flexWrap: "wrap", minWidth: 0 }}>
                <span>scopes:</span>
                <ScopeChips scopes={data.preflight.scopes} testId="instagram-go-live-scopes" />
              </div>
            ) : null}
          </div>
          <div style={{ marginTop: 14, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="text"
              placeholder="reason (recorded in audit log)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                padding: "8px 10px", borderRadius: 8,
                border: "1px solid var(--bo-border)",
                background: "var(--bo-bg-elev)", color: "var(--bo-ink)",
                flex: "1 1 220px", minWidth: 0, maxWidth: 320, fontSize: 13,
              }}
              data-testid="instagram-go-live-reason"
            />
            <button
              className={`bo-btn ${data.enabled ? "bo-btn-ghost" : "bo-btn-primary"}`}
              onClick={() => toggle(!data.enabled)}
              disabled={busy}
              data-testid="instagram-go-live-toggle"
            >
              {busy
                ? "Updating…"
                : data.enabled ? "Turn OFF" : "Turn ON"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
